import axios, { AxiosResponse } from 'axios'

import { endUpload, startUpload } from '../api/upload'

const uploadBlocks = async (file: File, sasUrl: string, blockIds: string[]) => {
  const blockSize = 4 * 1024 * 1024 // 4MB block size
  const totalBlocks = Math.ceil(file.size / blockSize)
  const promises: Promise<AxiosResponse>[] = []

  for (let i = 0; i < totalBlocks; i++) {
    const blockId = blockIds[i]
    const start = i * blockSize
    const end = Math.min(start + blockSize, file.size)
    const blockContent = file.slice(start, end)

    const promise = axios.put(
      `${sasUrl}&comp=block&blockid=${blockId}`,
      blockContent,
      {
        headers: {
          'Content-Type': file.type,
          'x-ms-blob-type': 'BlockBlob',
        },
      },
    )
    promises.push(promise)
  }
  await Promise.all(promises)
}

const commitBlockList = async (sasUrl: string, blockIds: string[]) => {
  const xmlPayload = `
    <BlockList>
      ${blockIds.map(blockId => `<Latest>${blockId}</Latest>`).join('\n')}
    </BlockList>
  `
  try {
    await axios.put(`${sasUrl}&comp=blocklist`, xmlPayload, {
      headers: {
        'Content-Type': 'application/xml',
        'x-ms-version': '2025-05-05',
      },
    })
  } catch (error) {
    console.error('Failed to commit block list:', error)
    throw new Error('Failed to commit block list')
  }
}

interface UploadResponse {
  id: string
  uploadUrl: string
}

export const uploadFile = async (
  file: File,
  organization_id: string,
): Promise<UploadResponse> => {
  try {
    const { uploadUrl, id } = await startUpload(file, organization_id)
    // file.size > 256mb
    if (file.size > 256 * 1024 * 1024) {
      const blockSize = 4 * 1024 * 1024 // 4MB block size (adjust as needed)
      const totalBlocks = Math.ceil(file.size / blockSize)
      const blockIds = []

      for (let i = 0; i < totalBlocks; i++) {
        const blockId = btoa(`block-${i}`)
        blockIds.push(blockId)
      }
      await uploadBlocks(file, uploadUrl, blockIds)
      await commitBlockList(uploadUrl, blockIds)
      const endResponse = await endUpload(id, organization_id)
      if (!endResponse.success) {
        throw new Error('Failed to upload file')
      }
      return { id, uploadUrl: endResponse.url }
    }
    const response = await axios.put(uploadUrl, file, {
      headers: {
        'x-ms-blob-type': 'BlockBlob',
        'Content-Type': file.type,
      },
    })
    if (response.status !== 201 && response.status !== 200) {
      throw new Error('Failed to upload file')
    }
    const endResponse = await endUpload(id, organization_id)
    if (!endResponse.success) {
      throw new Error('Failed to upload file')
    }
    return { id, uploadUrl: endResponse.url }
  } catch (error) {
    console.error('Error uploading file:', error)
    throw error
  }
}
